/**
 * The staff composer for a unit's public listing (step 11 — see CLAUDE.md's
 * Public listings site section). Publish/unpublish already existed as a
 * bare toggle in PropertyShow.tsx's units table; this is the form
 * UnitShow.tsx renders once a unit IS published, so there's something real
 * for the public homepage and the New Rentals page to display: a featured
 * photo, a gallery of additional photos, a short description, and an
 * advertised-rent override.
 *
 * One description field, one rent override, no rich text, no
 * preview-as-public-visitor — the gallery is the one place this composer
 * goes beyond "minimal," because the New Rentals page's large cards need
 * more than one photo per listing to be honest about what they show.
 */

import { useEffect, useState } from 'react';
import type { ListingPhotoDto } from 'shared';
import { CoverImage } from './CoverImage.js';
import { Field, FormErrors, TextArea, TextInput } from './Form.js';
import { api } from '../lib/api.js';
import { uploadGalleryPhoto, uploadListingPhoto } from '../lib/dataProvider.js';

export interface ListingSummary {
  id: string;
  publishedAt: string | null;
  description: string | null;
  advertisedRent: string | null;
  photoUrl: string | null;
}

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';

export function ListingComposer({
  unitId,
  listing,
  canEdit,
  onChange,
}: {
  unitId: string;
  listing: ListingSummary | null;
  canEdit: boolean;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [description, setDescription] = useState(listing?.description ?? '');
  const [advertisedRent, setAdvertisedRent] = useState(listing?.advertisedRent ?? '');
  const [gallery, setGallery] = useState<ListingPhotoDto[] | null>(null);

  // Re-sync local draft state when the listing identity changes (publish,
  // unpublish, or a save that came back with a different value).
  useEffect(() => {
    setDescription(listing?.description ?? '');
    setAdvertisedRent(listing?.advertisedRent ?? '');
  }, [listing?.id, listing?.description, listing?.advertisedRent]);

  useEffect(() => {
    if (!listing) {
      setGallery(null);
      return;
    }
    let cancelled = false;
    void api<{ data: ListingPhotoDto[] }>(`/listings/${listing.id}/photos`)
      .then((response) => {
        if (!cancelled) setGallery(response.data);
      })
      .catch(() => {
        if (!cancelled) setGallery([]);
      });
    return () => {
      cancelled = true;
    };
  }, [listing?.id]);

  if (!canEdit) return null;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChange();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function addGalleryPhoto(file: File) {
    if (!listing) return;
    setBusy(true);
    setError(null);
    try {
      const photo = await uploadGalleryPhoto(listing.id, file);
      setGallery((current) => [...(current ?? []), photo]);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function removeGalleryPhoto(photoId: string) {
    if (!listing) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/listings/${listing.id}/photos/${photoId}`, { method: 'DELETE' });
      setGallery((current) => (current ?? []).filter((p) => p.id !== photoId));
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>Public Listing</h2>
      <div className="card stack">
        {!listing ? (
          <>
            <p className="muted">Not published to the public listings site.</p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  api('/listings', { method: 'POST', body: JSON.stringify({ unitId }) }),
                )
              }
            >
              Publish
            </button>
          </>
        ) : (
          <>
            <div className="row">
              <span className="pill">Published</span>
              <span className="shell__spacer" />
              <button
                type="button"
                className="button button--quiet"
                disabled={busy}
                onClick={() =>
                  void run(() => api(`/listings/${listing.id}/unpublish`, { method: 'PATCH' }))
                }
              >
                Unpublish
              </button>
            </div>

            <CoverImage src={listing.photoUrl} alt="Listing featured photo" variant="card" />
            <Field
              label="Featured photo"
              name="listingPhoto"
              hint="JPEG, PNG, WebP, or HEIC · up to 25MB"
            >
              <input
                id="listingPhoto"
                name="listingPhoto"
                className="field__input"
                type="file"
                accept={PHOTO_ACCEPT}
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void run(() => uploadListingPhoto(listing.id, file));
                }}
              />
            </Field>

            <Field
              label="Gallery photos"
              name="galleryPhoto"
              hint="Shown alongside the featured photo on the New Rentals page. JPEG, PNG, WebP, or HEIC · up to 25MB each."
            >
              {gallery && gallery.length > 0 ? (
                <div className="listing-composer__gallery">
                  {gallery.map((photo) => (
                    <div key={photo.id} className="listing-composer__gallery-item">
                      <img src={photo.photoUrl} alt="" />
                      <button
                        type="button"
                        className="listing-composer__gallery-remove"
                        disabled={busy}
                        aria-label="Remove this photo"
                        onClick={() => void removeGalleryPhoto(photo.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <input
                id="galleryPhoto"
                name="galleryPhoto"
                className="field__input"
                type="file"
                accept={PHOTO_ACCEPT}
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void addGalleryPhoto(file);
                  // Cleared so the same file can be added again after a
                  // failed attempt — matches PhotoUpload.tsx's own reasoning.
                  event.target.value = '';
                }}
              />
            </Field>

            <Field label="Description" name="description">
              <TextArea name="description" value={description} onChange={setDescription} />
            </Field>
            {/* Equal housing nudge (CLAUDE.md, explicit): informational only —
                no validation, no blocking, no acknowledgment checkbox. */}
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              Reminder: listing copy should comply with equal housing laws.
            </p>

            <Field
              label="Advertised rent"
              name="advertisedRent"
              hint="Defaults to the unit's market rent at publish time."
            >
              <TextInput
                name="advertisedRent"
                value={advertisedRent}
                onChange={setAdvertisedRent}
              />
            </Field>

            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  api(`/listings/${listing.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ description, advertisedRent }),
                  }),
                )
              }
            >
              Save listing details
            </button>
          </>
        )}
        {error ? <FormErrors error={error} /> : null}
      </div>
    </section>
  );
}
